import { selectCurrentHotelId, selectIsInternalAdmin } from 'accessors'
import { updateCurrentHotel, updateHotelData } from 'actions/hotelDetails'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import PdfViewer from 'components/ReusableComponents/PdfViewerDialog/PdfViewer'
import EinHelpText from 'components/TaxInfoDialog/EinHelpText'
import EinHint from 'components/TaxInfoDialog/EinHint'
import EinInput from 'components/TaxInfoDialog/EinInput'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import contractApi from 'services/api/contracts'
import hotelApi from 'services/api/hotel'
import canadianProvinces from 'static/addresses/canadianProvinces'

import styles from './TaxInfoDialog.module.scss'

const TaxInfoDialog = ({
  onClose,
  delayRemindAfter,
  closeButtonText = 'Remind me later',
  defaultDoingBusinessAsName = '',
  defaultLegalBusinessName = '',
  defaultSupportEmail = '',
  defaultTaxId = ''
}) => {
  const { addToast } = useToasts()

  const dispatch = useDispatch()

  const [doingBusinessAsName, setDoingBusinessAsName] = useState(
    defaultDoingBusinessAsName
  )

  const [taxId, setTaxId] = useState(defaultTaxId)
  const [legalBusinessName, setLegalBusinessName] = useState(defaultLegalBusinessName)
  const [supportEmail, setSupportEmail] = useState(defaultSupportEmail)
  const currentHotelId = useSelector(selectCurrentHotelId)

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const [isLoading, setIsLoading] = useState(false)

  const [province, setProvince] = useState('')

  const [document, setDocument] = useState(null)

  function updateTaxInfo() {
    setIsLoading(true)

    hotelApi
      .updateAsUser({
        doingBusinessAsName,
        legalBusinessName,
        taxId,
        supportEmail
      })
      .then(res => {
        addToast('Details updated successfully!', { appearance: 'success' })
        dispatch(updateHotelData(currentHotelId, { ...res.subsidiary }))
        dispatch(updateCurrentHotel({ ...res.subsidiary }))
      })
      .catch(error => {
        if (isInternalAdmin) {
          addToast(
            `Something went wrong while updating the details: ${error.toString()}`,
            {
              appearance: 'error'
            }
          )
        } else {
          addToast('Details updated successfully!', { appearance: 'success' })
          delayRemindAfter()
        }
      })
      .finally(() => {
        setIsLoading(false)
        onClose()
      })
  }

  useEffect(() => {
    contractApi.documents
      .subsidiary()
      .then(data => setDocument((data || []).find(d => d.kind === 'LegalForm')))
      .catch(console.error)
  }, [])

  const cultureCode = useSelector(s => s.hotelDetails.cultureCode)

  return (
    <Dialog>
      <div className={isLoading ? styles.wrapperLoading : styles.wrapper}>
        <div className={styles.dialog}>
          <h2 className={styles.title}>
            Please provide the following information due to new Mobile carrier
            requirements
          </h2>
          {cultureCode === 'en-CA' && (
            <Dropdown
              label="Registered province"
              style={{ width: 400 }}
              options={canadianProvinces}
              defaultVal="Select province"
              value={province}
              onChange={e => setProvince(e.target.value)}
            />
          )}
          {cultureCode === 'en-US' && (
            <p className={styles.description}>
              Enter the highlighted information on W-9 form
            </p>
          )}
          <div className={styles.form}>
            <div className={styles.inputHolder}>
              <TextInput
                label="Name (as shown on income tax return)"
                style={{ width: 400 }}
                value={legalBusinessName}
                onChange={e => setLegalBusinessName(e.target.value)}
              />
            </div>
            <div className={styles.inputHolder}>
              <TextInput
                label="Business name"
                style={{ width: 400 }}
                value={doingBusinessAsName}
                onChange={e => setDoingBusinessAsName(e.target.value)}
              />
            </div>
            <div className={styles.inputHolder}>
              <TextInput
                label="Hotel support email"
                style={{ width: 400 }}
                value={supportEmail}
                onChange={e => setSupportEmail(e.target.value)}
              />
            </div>
            <div className={styles.inputHolder}>
              <EinInput setEin={setTaxId} ein={taxId} province={province} />
            </div>
          </div>
          <EinHint province={province} />
          <EinHelpText province={province} />
          <div className={styles.controls}>
            <SecondaryRegularButton onClick={onClose}>
              {closeButtonText}
            </SecondaryRegularButton>
            <PrimaryRegularButton
              disabled={
                !(doingBusinessAsName && legalBusinessName && supportEmail && taxId)
              }
              onClick={updateTaxInfo}
            >
              Save
            </PrimaryRegularButton>
          </div>
        </div>
        {cultureCode === 'en-US' &&
          (document ? (
            document.contentType === 'application/pdf' ? (
              <div className={styles.pdfViewerHolder}>
                <PdfViewer url={document.path} className={styles.w9form} />
              </div>
            ) : (
              <img src={document.path} className={styles.w9form} alt="W9 form" />
            )
          ) : (
            <div className={styles.w9form} />
          ))}
      </div>
    </Dialog>
  )
}

export default TaxInfoDialog
